import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { createPortal } from 'react-dom'

/**
 * Atraso dos tooltips de ferramenta do canvas. Curto o bastante para parecer
 * instantaneo ao pousar o mouse, longo o bastante para nao piscar quando o
 * ponteiro so atravessa a barra.
 */
export const TOOLBAR_TOOLTIP_DELAY_MS = 60

interface TooltipProps {
  content: ReactNode
  children: ReactNode
  open?: boolean
  delayMs?: number
}

export function Tooltip({ content, children, open = false, delayMs = 0 }: TooltipProps) {
  const [isVisible, setIsVisible] = useState(false)
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const anchorRef = useRef<HTMLSpanElement | null>(null)
  const tooltipRef = useRef<HTMLSpanElement | null>(null)

  useEffect(() => {
    if (open) {
      setIsVisible(true)
      return
    }

    setIsVisible(false)
  }, [open])

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [])

  useLayoutEffect(() => {
    if (!(open || isVisible) || !anchorRef.current) return

    const updatePosition = () => {
      const anchorRect = anchorRef.current?.getBoundingClientRect()
      const tooltipRect = tooltipRef.current?.getBoundingClientRect()
      if (!anchorRect) return

      const tooltipWidth = tooltipRect?.width ?? 0
      const viewportWidth = window.innerWidth
      const margin = 8
      const centeredLeft = anchorRect.left + anchorRect.width / 2 - tooltipWidth / 2
      const maxLeft = Math.max(margin, viewportWidth - tooltipWidth - margin)
      const clampedLeft = Math.min(Math.max(centeredLeft, margin), maxLeft)

      setPosition({
        top: anchorRect.top - 8,
        left: clampedLeft,
      })
    }

    updatePosition()
    window.addEventListener('scroll', updatePosition, true)
    window.addEventListener('resize', updatePosition)
    return () => {
      window.removeEventListener('scroll', updatePosition, true)
      window.removeEventListener('resize', updatePosition)
    }
  }, [open, isVisible])

  const showTooltip = () => {
    if (open) return
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => {
      setIsVisible(true)
    }, delayMs)
  }

  const hideTooltip = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
    if (!open) {
      setIsVisible(false)
    }
  }

  return (
    <span
      ref={anchorRef}
      className="relative flex min-w-0"
      onMouseEnter={showTooltip}
      onMouseLeave={hideTooltip}
      onFocus={showTooltip}
      onBlur={hideTooltip}
    >
      <span className="min-w-0 flex-1">{children}</span>
      {open || isVisible
        ? createPortal(
            <span
              ref={tooltipRef}
              role="tooltip"
              className="pointer-events-none fixed z-[3000] max-w-[min(24rem,calc(100vw-16px))] rounded-md border px-2 py-1 text-[10px]"
              style={{
                top: position?.top ?? 0,
                left: position?.left ?? 0,
                transform: 'translateY(-100%)',
                background: 'var(--bg-popup)',
                borderColor: 'var(--border)',
                color: 'var(--text)',
              }}
            >
              {content}
            </span>,
            document.body,
          )
        : null}
    </span>
  )
}
